/**
 * GRUPO BMP — INSTITUTIONAL NAVIGATION STRUCTURE
 *
 * Single source of truth for sidebar ordering, dashboard rendering and
 * module discovery. Strict phase order P0 → P7, no duplicates, no legacy.
 *
 * EN-First | Production-Ready | Scalable
 * Last Updated: 2026-04-28 — Dashboard Harmonization Pass
 */

import {
  // CRM Icons
  Users, UserCircle, Building2, Target, TrendingUp, GitBranch,
  FolderOpen, CheckSquare, StickyNote, Activity, Star, XCircle,
  Webhook,

  // Finance Icons
  DollarSign, FileText, CreditCard, Receipt, BarChart3,

  // Procurement Icons
  ShoppingCart, Truck, Package, FileSignature, ShieldAlert,

  // Products Icons
  FolderTree, Tags, Calculator,

  // Risk & Compliance Icons
  Shield, AlertTriangle, ShieldCheck, Bell,

  // CMS Icons
  Home, Image, Info, Layers, Briefcase, BookOpen, Zap,

  // System Icons
  GitCommit, Calendar,

  // BMP Assistant Icons
  Bot, MessageCircle, Repeat, Lightbulb, Cpu, List,

  // Onboarding Icons
  Inbox, UserPlus, Sparkles, Flame,

  // Brand Studio Icons
  Palette, Globe, Send,

  // IT Solutions Icons
  Laptop, Mail,
} from 'lucide-react';


/**
 * NAVIGATION_STRUCTURE
 * Keys are declared in strict phase order to ensure sidebar + dashboard
 * render P0 → P7 without additional sorting.
 *
 * Each section:
 *   - code: phase code (P0..P7) — authoritative identifier
 *   - label: display name
 *   - icon: Lucide component
 *   - status: 'complete' | 'in_progress' | 'planned'
 *   - description: short institutional description
 *   - modules: array of { id, label, path, icon, enabled, version, description }
 */
export const NAVIGATION_STRUCTURE = {

  // ===================================================================
  // P0 — WEBSITE CMS
  // ===================================================================
  WEBSITE_CMS: {
    code: "P0",
    phase: "P0",
    label: "Website CMS",
    icon: Home,
    status: "complete",
    description: "Content management for public website with auto-translation",
    modules: [
      { id: "homepage-hero", label: "Hero Section", path: "/admin/homepage/hero", icon: Image, enabled: true, version: "1.0.0", description: "Homepage hero section editor" },
      { id: "homepage-about", label: "About Section", path: "/admin/homepage/about", icon: Info, enabled: true, version: "1.0.0", description: "About section content" },
      { id: "homepage-services", label: "Services", path: "/admin/homepage/services", icon: Layers, enabled: true, version: "1.0.0", description: "Services showcase" },
      { id: "homepage-projects", label: "Projects", path: "/admin/homepage/projects", icon: Briefcase, enabled: true, version: "1.0.0", description: "Project portfolio" },
      { id: "homepage-stats", label: "Stats", path: "/admin/homepage/stats", icon: BarChart3, enabled: true, version: "1.0.0", description: "Statistics section" },
      { id: "homepage-clients", label: "Clients", path: "/admin/homepage/clients", icon: Users, enabled: true, version: "1.0.0", description: "Client showcase" },
      { id: "homepage-insights", label: "Insights", path: "/admin/homepage/insights", icon: BookOpen, enabled: true, version: "1.0.0", description: "Blog and insights" },
      { id: "homepage-highlight-trading", label: "Highlight: Trading", path: "/admin/homepage/highlights/trading", icon: Zap, enabled: true, version: "1.0.0", description: "Trading highlight section" },
      { id: "homepage-highlight-procurement", label: "Highlight: Procurement", path: "/admin/homepage/highlights/procurement", icon: Zap, enabled: true, version: "1.0.0", description: "Procurement highlight section" },
      { id: "homepage-cta", label: "Call to Action", path: "/admin/homepage/cta", icon: Target, enabled: true, version: "1.0.0", description: "CTA section" },
      { id: "homepage-footer", label: "Footer", path: "/admin/homepage/footer", icon: Home, enabled: true, version: "1.0.0", description: "Footer content" },
      { id: "schedule-manager", label: "Schedule Manager", path: "/admin/schedule", icon: Calendar, enabled: true, version: "1.0.0", description: "Meeting availability & booking requests" },
      { id: "cms-document-templates", label: "Document Templates", path: "/admin/cms/document-templates", icon: FileText, enabled: true, version: "1.1.0", description: "LOI, NDA, NCNDA, SPA, Cover Letters, Partnership templates" },
      { id: "cms-payment-terms", label: "Payment Terms", path: "/admin/cms/payment-terms", icon: CreditCard, enabled: true, version: "1.1.0", description: "Reusable payment-condition snippets" },
      { id: "cms-pdf-preview-gallery", label: "PDF Preview Gallery", path: "/admin/cms/pdf-preview-gallery", icon: FileText, enabled: true, version: "1.0.0", description: "Live sample preview for all premium PDF generators" },
    ],
  },

  // ===================================================================
  // P0.5 — BRAND STUDIO (Corporate Identity Service)
  // ===================================================================
  BRAND_STUDIO: {
    code: "P0.5",
    phase: "P0.5",
    label: "Brand Studio",
    icon: Sparkles,
    status: "complete",
    description: "AI-powered corporate identity service — brand book, logos, website handover",
    modules: [
      { id: "brand-studio-home", label: "Projects & Plans", path: "/admin/brand-studio", icon: Palette, enabled: true, version: "1.0.0", description: "Brand projects list, pricing plans and admin review queue" },
      { id: "brand-studio-public", label: "Public Page", path: "/brand-studio", icon: Globe, enabled: true, version: "1.0.0", description: "Open the public pricing & sales page (new tab)" },
    ],
  },

  // ===================================================================
  // P0.6 — OPPORTUNITY DISTRIBUTION CENTER
  // ===================================================================
  ODC: {
    code: "P0.6",
    phase: "P0.6",
    label: "Opportunity Desk",
    icon: Sparkles,
    status: "complete",
    description: "Capture inbound opportunities, match with CRM and dispatch institutional outreach",
    modules: [
      { id: "odc-home", label: "Opportunities & Templates", path: "/admin/odc", icon: Palette, enabled: true, version: "1.0.0", description: "Opportunity list, match engine and email templates" },
    ],
  },

  // ===================================================================
  // P0.7 — IT SOLUTIONS (Public IT Catalogue + RFQ Desk)
  // ===================================================================
  IT_SOLUTIONS: {
    code: "P0.7",
    phase: "P0.7",
    label: "IT Solutions",
    icon: Laptop,
    status: "complete",
    description: "Public IT catalogue, multi-brand curation, RFQ inbox and quote-to-CRM lead pipeline",
    modules: [
      { id: "it-catalogue", label: "Catalogue", path: "/admin/it-catalogue", icon: Package, enabled: true, version: "1.0.0", description: "Curate products, brands, categories, hero images and featured order" },
      { id: "it-rfq-inbox", label: "RFQ Inbox", path: "/admin/it-catalogue/rfq", icon: Inbox, enabled: true, version: "1.0.0", description: "Incoming quote requests from /it-solutions page" },
      { id: "it-public-page", label: "Public Page CMS", path: "/admin/it-catalogue/public-page", icon: Globe, enabled: true, version: "1.0.0", description: "Edit hero, categories, how-we-deliver and CTA on /it-solutions" },
    ],
  },

  // ===================================================================
  // P1 — FINANCIAL SYSTEM
  // ===================================================================
  FINANCE: {
    code: "P1",
    phase: "P1",
    label: "Financial System",
    icon: DollarSign,
    status: "complete",
    description: "UAE-compliant invoicing, payments, FTA filing and reporting",
    modules: [
      { id: "finance-dashboard", label: "Dashboard", path: "/admin/finance/dashboard", icon: BarChart3, enabled: true, version: "1.0.0", description: "Financial KPIs and metrics" },
      { id: "invoices", label: "Invoices", path: "/admin/finance/invoices", icon: FileText, enabled: true, version: "2.0.0", description: "UAE-compliant invoice creation" },
      { id: "fta-filing", label: "FTA Filing", path: "/admin/finance/fta-filing", icon: FileText, enabled: true, version: "1.0.0", description: "UAE corporate tax filing preparation" },
      { id: "finance-customers", label: "Customers", path: "/admin/finance/customers", icon: Users, enabled: true, version: "1.0.0", description: "Customer master data with TRN" },
      { id: "finance-products", label: "Products", path: "/admin/finance/products", icon: Tags, enabled: false, version: "1.0.0", description: "Moved to Master Product Registry — see Products System" },
      { id: "finance-services", label: "Services", path: "/admin/finance/services", icon: Calculator, enabled: true, version: "1.0.0", description: "Service catalog with unit pricing" },
      { id: "payments", label: "Payments", path: "/admin/finance/payments", icon: CreditCard, enabled: true, version: "1.0.0", description: "Payment processing and tracking" },
      { id: "receipts", label: "Receipts", path: "/admin/finance/receipts", icon: Receipt, enabled: true, version: "1.0.0", description: "Receipt generation" },
      { id: "transactions", label: "Transactions", path: "/admin/finance/transactions", icon: List, enabled: true, version: "1.0.0", description: "Transaction history" },
      { id: "subscriptions", label: "Subscriptions", path: "/admin/finance/subscriptions", icon: GitCommit, enabled: true, version: "1.0.0", description: "Recurring billing management" },
      { id: "reports-sales", label: "Sales Report", path: "/admin/finance/reports/sales", icon: TrendingUp, enabled: true, version: "1.0.0", description: "Sales analytics" },
      { id: "reports-profit", label: "Profit Report", path: "/admin/finance/reports/profit", icon: DollarSign, enabled: true, version: "1.0.0", description: "Profit & loss analytics" },
      { id: "reports-cashflow", label: "Cash Flow Report", path: "/admin/finance/reports/cashflow", icon: Activity, enabled: true, version: "1.0.0", description: "Cash flow analysis" },
    ],
  },

  // ===================================================================
  // P2 — CRM SYSTEM
  // ===================================================================
  CRM: {
    code: "P2",
    phase: "P2",
    label: "CRM System",
    icon: Users,
    status: "complete",
    description: "Customer Relationship Management with reputation tracking and blacklist",
    modules: [
      { id: "crm-dashboard", label: "CRM Dashboard", path: "/admin/crm/dashboard", icon: BarChart3, enabled: true, version: "1.0.0", description: "CRM analytics and KPIs" },
      { id: "master-counterparties", label: "Master Counterparties", path: "/admin/counterparties", icon: Users, enabled: true, version: "1.0.0", description: "Canonical registry for Suppliers, Clients, Partners, Leads" },
      { id: "contacts", label: "Contacts", path: "/admin/crm/contacts", icon: UserCircle, enabled: true, version: "1.0.0", description: "Contact database" },
      { id: "clients", label: "Clients", path: "/admin/crm/clients", icon: Building2, enabled: true, version: "1.0.0", description: "Client management" },
      { id: "leads", label: "Leads", path: "/admin/crm/leads", icon: Target, enabled: true, version: "1.0.0", description: "Lead pipeline with scoring" },
      { id: "lead-sources", label: "Hot Leads Webhook", path: "/admin/crm/lead-sources", icon: Webhook, enabled: true, version: "2.0.0", description: "Public signed webhooks for Website + LinkedIn + Meta Lead Ads + Zapier" },
      { id: "opportunities", label: "Opportunities", path: "/admin/crm/opportunities", icon: TrendingUp, enabled: true, version: "1.0.0", description: "Sales opportunities" },
      { id: "pipeline", label: "Pipeline", path: "/admin/crm/pipeline", icon: GitBranch, enabled: true, version: "1.0.0", description: "Kanban-style sales pipeline" },
      { id: "crm-jv-pipeline", label: "JV / Deals Pipeline", path: "/admin/crm/jv-pipeline", icon: Briefcase, enabled: true, version: "1.0.0", description: "Issued → Signed → Executed → Completed" },
      { id: "crm-generated-documents", label: "Generated Documents", path: "/admin/crm/generated-documents", icon: FileText, enabled: true, version: "1.0.0", description: "Audit trail — every document generated from a template" },
      { id: "documents", label: "Documents", path: "/admin/crm/documents", icon: FolderOpen, enabled: true, version: "1.0.0", description: "Document management per client" },
      { id: "tasks", label: "Tasks", path: "/admin/crm/tasks", icon: CheckSquare, enabled: true, version: "1.0.0", description: "Task and to-do management" },
      { id: "notes", label: "Notes", path: "/admin/crm/notes", icon: StickyNote, enabled: true, version: "1.0.0", description: "Rich text notes with tags" },
      { id: "activity-timeline", label: "Activity Timeline", path: "/admin/crm/activity", icon: Activity, enabled: true, version: "1.0.0", description: "Automatic activity tracking" },
      { id: "client-reputation", label: "Client Reputation", path: "/admin/crm/reputation", icon: ShieldCheck, enabled: true, version: "1.0.0", description: "Client reputation scoring" },
      { id: "client-blacklist", label: "Blacklist", path: "/admin/crm/blacklist", icon: XCircle, enabled: true, version: "1.0.0", description: "Blacklisted entities management" },
    ],
  },

  // ===================================================================
  // P3 — BMP ASSISTANT
  // ===================================================================
  BMP_ASSISTANT: {
    code: "P3",
    phase: "P3",
    label: "BMP Assistant",
    icon: Bot,
    status: "complete",
    description: "AI-powered executive assistant for platform management",
    modules: [
      { id: "assistant-overview", label: "Overview", path: "/admin/assistant", icon: Bot, enabled: true, version: "1.0.0", description: "Assistant dashboard and quick access" },
      { id: "assistant-chat", label: "Chat", path: "/admin/assistant/chat", icon: MessageCircle, enabled: true, version: "1.0.0", description: "Conversational AI interface" },
      { id: "assistant-actions", label: "Actions", path: "/admin/assistant/actions", icon: Zap, enabled: true, version: "1.0.0", description: "Quick actions and workflows" },
      { id: "assistant-automations", label: "Automations", path: "/admin/assistant/automations", icon: Repeat, enabled: true, version: "1.0.0", description: "Automated tasks and routines" },
      { id: "assistant-insights", label: "Insights", path: "/admin/assistant/insights", icon: Lightbulb, enabled: true, version: "1.0.0", description: "AI-powered business insights" },
      { id: "assistant-alerts", label: "Alerts", path: "/admin/assistant/alerts", icon: AlertTriangle, enabled: true, version: "1.0.0", description: "Smart notifications and warnings" },
      { id: "assistant-system", label: "System Intelligence", path: "/admin/assistant/system", icon: Cpu, enabled: true, version: "1.0.0", description: "Platform intelligence" },
    ],
  },

  // ===================================================================
  // P4 — PRODUCTS SYSTEM
  // ===================================================================
  PRODUCTS: {
    code: "P4",
    phase: "P4",
    label: "Products System",
    icon: Package,
    status: "complete",
    description: "Master product registry feeding Procurement, Finance, CRM and Inventory",
    modules: [
      { id: "products", label: "Products", path: "/admin/products", icon: Package, enabled: true, version: "1.0.0", description: "Master product catalog with SKU, pricing, cost and attributes" },
      { id: "catalogs", label: "Catalogs", path: "/admin/catalogs", icon: FolderOpen, enabled: true, version: "1.0.0", description: "Import products from any URL, build branded catalogues and raise proformas & invoices" },
      { id: "product-categories", label: "Categories", path: "/admin/products/categories", icon: FolderTree, enabled: false, version: "0.0.0", description: "Hierarchical product categories" },
      { id: "price-lists", label: "Price Lists", path: "/admin/products/prices", icon: Tags, enabled: false, version: "0.0.0", description: "Multiple price lists for regions and channels" },
      { id: "product-variants", label: "Variants", path: "/admin/products/variants", icon: Layers, enabled: false, version: "0.0.0", description: "Product variants such as size, colour, packaging" },
      { id: "cost-structure", label: "Cost Structure", path: "/admin/products/costs", icon: Calculator, enabled: false, version: "0.0.0", description: "Cost breakdown incl. logistics, duties and margin" },
    ],
  },

  // ===================================================================
  // P5 — RISK & COMPLIANCE
  // ===================================================================
  RISK_COMPLIANCE: {
    code: "P5",
    phase: "P5",
    label: "Risk & Compliance",
    icon: ShieldAlert,
    status: "in_progress",
    description: "KYC, Sanctions screening, Risk scoring and institutional alerts",
    modules: [
      { id: "compliance-center", label: "Compliance Center", path: "/admin/risk/compliance-center", icon: ShieldAlert, enabled: true, version: "1.0.0", description: "Gating policy, automated sweeps, gate checker" },
      { id: "aml-pep-edd", label: "AML / PEP / EDD", path: "/admin/risk/aml", icon: AlertTriangle, enabled: true, version: "1.0.0", description: "Transaction monitoring, PEP screening, Enhanced Due Diligence workflow" },
      { id: "risk-scoring", label: "Risk Scoring", path: "/admin/risk/scoring", icon: AlertTriangle, enabled: true, version: "1.0.0", description: "Automated risk scoring for counterparties and products" },
      { id: "kyc-kyb", label: "KYC / KYB", path: "/admin/risk/kyc", icon: Shield, enabled: true, version: "1.0.0", description: "Know Your Customer / Know Your Business" },
      { id: "alerts-flags", label: "Alerts & Flags", path: "/admin/risk/alerts", icon: Bell, enabled: true, version: "1.0.0", description: "Risk alerts and compliance flags" },
    ],
  },

  // ===================================================================
  // P6 — PROCUREMENT
  // ===================================================================
  PROCUREMENT: {
    code: "P6",
    phase: "P6",
    label: "Procurement",
    icon: ShoppingCart,
    status: "in_progress",
    description: "Enterprise procurement — RFQ, PO, GRN, Inventory, OCR intake",
    modules: [
      { id: "procurement-dashboard", label: "Dashboard", path: "/admin/procurement/dashboard", icon: BarChart3, enabled: true, version: "1.0.0", description: "Executive view — POs, GRNs, Invoices, Suppliers" },
      { id: "contracts", label: "Contract Center", path: "/admin/procurement/contracts", icon: FileSignature, enabled: true, version: "1.0.0", description: "Supplier contracts, validity and linkage to RFQ/PO" },
      { id: "suppliers", label: "Suppliers / Partners", path: "/admin/procurement/suppliers", icon: Truck, enabled: true, version: "1.1.0", description: "Suppliers, commercial partners, financiers" },
      { id: "supplier-score", label: "Supplier Score", path: "/admin/procurement/suppliers/ranking", icon: Star, enabled: true, version: "1.0.0", description: "Supplier performance ranking" },
      { id: "companies", label: "Companies", path: "/admin/procurement/companies", icon: Building2, enabled: true, version: "1.0.0", description: "Issuing companies and bank accounts" },
      { id: "purchase-orders", label: "Purchase Orders", path: "/admin/procurement/purchase-orders", icon: ShoppingCart, enabled: true, version: "1.0.0", description: "Enterprise PO management" },
      { id: "purchase-requests", label: "Purchase Requests", path: "/admin/procurement/purchase-requests", icon: FileText, enabled: true, version: "1.0.0", description: "PR approval workflow" },
      { id: "inventory", label: "Inventory", path: "/admin/procurement/inventory", icon: Package, enabled: true, version: "1.0.0", description: "Multi-warehouse stock management" },
      { id: "invoices-payments", label: "Invoices & Payments", path: "/admin/procurement/invoices", icon: Receipt, enabled: true, version: "1.0.0", description: "Multi-currency invoicing" },
      { id: "grns", label: "Goods Received (GRN)", path: "/admin/procurement/grns", icon: Package, enabled: true, version: "1.0.0", description: "Record deliveries against a PO" },
      { id: "rfqs", label: "RFQs", path: "/admin/procurement/rfqs", icon: FileSignature, enabled: true, version: "1.0.0", description: "Request for Quotation workflows" },
      { id: "supplier-ocr", label: "OCR Intake", path: "/admin/procurement/ocr", icon: FileText, enabled: true, version: "1.0.0", description: "Automated parsing of supplier invoices and proposals" },
    ],
  },

  // ===================================================================
  // P7 — AUTOMATION LAYER
  // ===================================================================
  AUTOMATION_LAYER: {
    code: "P7",
    phase: "P7",
    label: "Automation Layer",
    icon: Repeat,
    status: "in_progress",
    description: "Workflow engine, triggers, background jobs and notification hub",
    modules: [
      { id: "automation-dashboard", label: "Automation Dashboard", path: "/admin/automation/dashboard", icon: BarChart3, enabled: true, version: "1.0.0", description: "KPIs, recent executions, top workflows, top events" },
      { id: "automation-workflows", label: "Workflows", path: "/admin/automation/workflows", icon: Repeat, enabled: true, version: "1.0.0", description: "Define trigger → conditions → actions automations" },
      { id: "automation-tasks", label: "Task Center", path: "/admin/automation/tasks", icon: CheckSquare, enabled: true, version: "1.0.0", description: "Manual + automation-generated tasks" },
      { id: "automation-triggers", label: "Triggers", path: "/admin/automation/triggers", icon: Zap, enabled: false, version: "0.0.0", description: "Event-based trigger registry" },
      { id: "automation-jobs", label: "Background Jobs", path: "/admin/automation/jobs", icon: Cpu, enabled: false, version: "0.0.0", description: "Scheduled background job management" },
      { id: "automation-notifications", label: "Notification Hub", path: "/admin/automation/notifications", icon: Bell, enabled: false, version: "0.0.0", description: "Unified cross-module notifications" },
    ],
  },
  // ===================================================================
  ONBOARDING: {
    code: "P8",
    phase: "P8",
    label: "Onboarding",
    icon: UserPlus,
    status: "in_progress",
    description: "Intelligent registration, OTP-secured form, AI classifier and approval inbox",
    modules: [
      { id: "onboarding-submissions", label: "Submissions Inbox", path: "/admin/onboarding/submissions", icon: Inbox, enabled: true, version: "1.0.0", description: "Review, approve, reject or request more info on new registrations" },
      { id: "onboarding-tokens",      label: "Invite Tokens",      path: "/admin/onboarding/tokens",      icon: Sparkles, enabled: true, version: "1.0.0", description: "Generate single-use or multi-use registration links" },
      { id: "onboarding-leads",       label: "Hot Leads Board",    path: "/admin/onboarding/leads",       icon: Flame, enabled: true, version: "1.0.0", description: "Approved registrations with AI score above threshold — warm pipeline" },
    ],
  },
  // ===================================================================
  // PR — PLATFORM RESOURCES (Owner / Admin only)
  // ===================================================================
  PLATFORM_RESOURCES: {
    code: "PR",
    phase: "PR",
    label: "Platform Resources",
    icon: Shield,
    status: "in_progress",
    description: "Access control, RBAC, audit trail, system management",
    requiredRoles: ["owner", "admin_l2"],
    modules: [
      { id: "access-control", label: "Access Control Center", path: "/admin/access-control", icon: Shield, enabled: true, version: "1.0.0", description: "Approve, promote, demote, block and audit accounts. Owner-master audit log.", requiredRoles: ["owner", "admin_l2"] },
    ],
  },
};


// ==========================================================================
// Helper functions
// ==========================================================================
export const getEnabledModules = () => {
  const enabled = [];
  Object.keys(NAVIGATION_STRUCTURE).forEach(sectionKey => {
    const section = NAVIGATION_STRUCTURE[sectionKey];
    section.modules.forEach(module => {
      if (module.enabled) enabled.push({ ...module, section: section.label, sectionKey, phase: section.phase });
    });
  });
  return enabled;
};

export const getAllModules = () => {
  const all = [];
  Object.keys(NAVIGATION_STRUCTURE).forEach(sectionKey => {
    const section = NAVIGATION_STRUCTURE[sectionKey];
    section.modules.forEach(module => {
      all.push({ ...module, section: section.label, sectionKey, phase: section.phase });
    });
  });
  return all;
};

export const getModulesBySection = (sectionKey) =>
  NAVIGATION_STRUCTURE[sectionKey]?.modules || [];

export const getSectionByCode = (code) =>
  Object.values(NAVIGATION_STRUCTURE).find(s => s.code === code);

export const getModuleStats = () => {
  const all = getAllModules();
  return {
    total: all.length,
    enabled: all.filter(m => m.enabled).length,
    disabled: all.filter(m => !m.enabled).length,
  };
};

export const getSectionStats = () => {
  const sections = Object.values(NAVIGATION_STRUCTURE);
  return {
    total: sections.length,
    complete: sections.filter(s => s.status === 'complete').length,
    inProgress: sections.filter(s => s.status === 'in_progress').length,
    planned: sections.filter(s => s.status === 'planned').length,
  };
};

export default NAVIGATION_STRUCTURE;
